import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Container,
  Box,
  Paper,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Table,
  Skeleton,
} from '@mui/material';
import InfiniteScroll from 'react-infinite-scroller';

import { useGetPuzzleItemsQuery } from 'src/api/puzzle-item/getPuzzleItems';
import { useDeletePuzzleItemMutation } from 'src/api/puzzle-item/deletePuzzleItem';
import { ActionButton } from 'src/components/common/ActionButton';
import DeleteDialog from 'src/components/pickem-admin/DeleteDialog';
import PuzzleItemDialog from '@/components/PuzzleItems/puzzleItems.modal';
import Iconify from 'src/components/iconify';
import { useTableColumns } from 'src/hooks/useTableColumns';
import { useTimezoneStore } from 'src/store/timezoneStore';
import { useGlobalFiltersStore } from 'src/store/globalFilters.store';
import { dateUtils } from 'src/utils/date';

export default function PuzzleItems() {
  const { timezone } = useTimezoneStore();
  const { languageCode } = useGlobalFiltersStore();

  const [open, setOpen] = useState<HTMLElement | null>(null);
  const [openId, setOpenId] = useState<number>(-1);
  const [addItem, setAddItem] = useState<boolean>(false);
  const [editItemId, setEditItemId] = useState<number>(-1);
  const [deleteItemId, setDeleteItemId] = useState<number>(-1);

  // API
  const {
    data: puzzleItems = [],
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useGetPuzzleItemsQuery({
    languageCode,
  });

  const { mutateAsync: deletePuzzleItemMutation, isPending: isDeleteLoading } =
    useDeletePuzzleItemMutation();

  const deleteItemHandler = async (id: number) => {
    setOpenId(-1);

    await deletePuzzleItemMutation(id);
  };

  const handleMenu = (event: any) => {
    if (event?.currentTarget?.id) {
      setOpen(event.currentTarget);
      setOpenId(Number(event.currentTarget.id));
    } else {
      setOpen(null);
      setOpenId(-1);
    }
  };

  const closeDeleteModal = () => {
    setOpenId(-1);
    setDeleteItemId(-1);
  };

  const closeSaveEditModal = () => {
    setOpenId(-1);
    setAddItem(false);
    setEditItemId(-1);
  };

  const { puzzleItemsColumns } = useTableColumns();

  return (
    <>
      <Helmet>
        <title>Puzzle Items</title>
      </Helmet>
      <Container>
        <Box>
          <Button
            variant='contained'
            startIcon={<Iconify icon='eva:plus-fill' />}
            onClick={() => setAddItem(true)}
          >
            New Item
          </Button>
        </Box>
        <Box sx={{ width: '100%', mt: 3 }}>
          <InfiniteScroll
            pageStart={0}
            loadMore={() => fetchNextPage()}
            hasMore={hasNextPage}
            loader={
              <div className='loader' key='loader'>
                Loading ...
              </div>
            }
          >
            {isLoading ? (
              <Skeleton width='100%' height={120} />
            ) : (
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label='puzzle items table'>
                  <TableHead>
                    <TableRow>
                      {puzzleItemsColumns.map((header) => {
                        return (
                          <TableCell key={header.id} align={header.align}>
                            {header.label}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {puzzleItems?.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        {puzzleItemsColumns.map((header) => {
                          switch (header.id) {
                            case 'createdAt':
                              return (
                                <TableCell key={header.id} align={header.align}>
                                  {dateUtils.formatForDisplay(
                                    row.createdAt,
                                    timezone
                                  )}
                                </TableCell>
                              );
                            case 'actions':
                              return (
                                <TableCell key={header.id} align={header.align}>
                                  <ActionButton
                                    id={String(row.id)}
                                    onClick={handleMenu}
                                    open={open}
                                    setEditItemId={setEditItemId}
                                    setDeleteItemId={setDeleteItemId}
                                    openId={openId}
                                  />
                                </TableCell>
                              );
                            default:
                              return (
                                <TableCell key={header.id} align={header.align}>
                                  {row[header.id as keyof typeof row]}
                                </TableCell>
                              );
                          }
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </InfiniteScroll>
        </Box>
      </Container>

      {(addItem || editItemId !== -1) && (
        <PuzzleItemDialog
          open={addItem || editItemId !== -1}
          item={puzzleItems?.find((item) => item.id === openId)}
          onClose={closeSaveEditModal}
        />
      )}

      {deleteItemId !== -1 && (
        <DeleteDialog
          openConfirm={deleteItemId !== -1}
          type='puzzle item'
          index={deleteItemId}
          setOpenConfirm={closeDeleteModal}
          deleteGameHandler={deleteItemHandler}
          isLoading={isDeleteLoading}
        />
      )}
    </>
  );
}
